// src/components/Home.js
import React, {useState, useContext, useEffect, useRef} from 'react';
import {
    Container,
    Card,
    CardContent,
    CardHeader,
    TextField,
    Button,
    Typography,
    Grid,
    Switch,
    FormControlLabel,
    AppBar,
    Toolbar,
    Radio,
    RadioGroup,
    FormControl,
    FormLabel, Grid2,
} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {fetchScores} from '../services/api';
import {AuthContext} from '../context/AuthContext';
import ProgressBar from './ProgressBar';
import RowDataChart from "./RowDataChart";
import OccupiedCapacityChart from "./OccupiedCapacityChart";
import SimpleGameTimeOccupancyChart from "./SimpleGameTimeOccupancyChart";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Helper to format dates in "MM/DD/YYYY HH:MM:SS" format.
const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return '';
    // Format: "YYYY-MM-DD HH:MM:SS.mmm"
    // Remove the "T" and trailing "Z" from ISO string.
    return d.toISOString().replace('T', ' ').replace('Z', '');
};

// Helper to format only date in "MM/DD/YYYY" format.
const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return '';
    // Return only date part "YYYY-MM-DD"
    return d.toISOString().split('T')[0];
};

const formatDateTimeForFilename = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year}-${hours}:${minutes}`;
};

const processDailySummary = (data) => {
    const summary = {};
    data.forEach((item) => {
        const createdAt = item.attributes.createdAt;
        if (!createdAt) return;
        // Format group as MM/DD/YYYY
        const day = formatDate(createdAt);
        const game =
            item.attributes.game_variant &&
            item.attributes.game_variant.data &&
            item.attributes.game_variant.data.attributes &&
            item.attributes.game_variant.data.attributes.name
                ? item.attributes.game_variant.data.attributes.name
                : 'Unknown';
        // Use score field as numberOfPlayers; if null, use 0.
        const players = item.attributes.score ?? 0;
        if (!summary[day]) summary[day] = {sessions: {}, players: 0};
        summary[day].sessions[game] = (summary[day].sessions[game] || 0) + 1;
        summary[day].players += players;
    });
    return summary;
};

const processSuccessSummary = (data) => {
    const summary = {};
    data.forEach((item) => {
        if (!item.attributes.gameEndDate) return;
        const start = new Date(item.attributes.gameStartDate);
        const end = new Date(item.attributes.gameEndDate);
        if (((end - start) / 1000) <= 40) return;
        const createdAt = item.attributes.createdAt;
        if (!createdAt) return;
        const day = formatDate(createdAt);
        const game =
            item.attributes.game_variant &&
            item.attributes.game_variant.data &&
            item.attributes.game_variant.data.attributes &&
            item.attributes.game_variant.data.attributes.name
                ? item.attributes.game_variant.data.attributes.name
                : 'Unknown';
        const players = item.attributes.score ?? 0;
        if (!summary[day]) summary[day] = {sessions: {}, players: 0};
        summary[day].sessions[game] = (summary[day].sessions[game] || 0) + 1;
        summary[day].players += players;
    });
    return summary;
};

const processHourlySummary = (data) => {
    const summary = {};
    data.forEach((item) => {
        const createdAt = item.attributes.createdAt;
        if (!createdAt) return;
        const dateObj = new Date(createdAt);
        if (isNaN(dateObj)) return;
        // For hourly grouping, format as "MM/DD/YYYY HH"
        const date = formatDate(createdAt);
        const hour = dateObj.getUTCHours(); // or getHours() if local time preferred
        const dateHour = `${date} ${hour.toString().padStart(2, '0')}`;
        const game =
            item.attributes.game_variant &&
            item.attributes.game_variant.data &&
            item.attributes.game_variant.data.attributes &&
            item.attributes.game_variant.data.attributes.name
                ? item.attributes.game_variant.data.attributes.name
                : 'Unknown';
        const players = item.attributes.score ?? 0;
        if (!summary[dateHour]) summary[dateHour] = {sessions: {}, players: 0};
        summary[dateHour].sessions[game] = (summary[dateHour].sessions[game] || 0) + 1;
        summary[dateHour].players += players;
    });
    return summary;
};

const processHourlySuccessSummary = (data) => {
    const summary = {};
    data.forEach((item) => {
        if (!item.attributes.gameEndDate) return;
        const start = new Date(item.attributes.gameStartDate);
        const end = new Date(item.attributes.gameEndDate);
        if (((end - start) / 1000) <= 40) return;
        const createdAt = item.attributes.createdAt;
        if (!createdAt) return;
        const dateObj = new Date(createdAt);
        if (isNaN(dateObj)) return;
        const date = formatDate(createdAt);
        const hour = dateObj.getUTCHours();
        const dateHour = `${date} ${hour.toString().padStart(2, '0')}`;
        const game =
            item.attributes.game_variant &&
            item.attributes.game_variant.data &&
            item.attributes.game_variant.data.attributes &&
            item.attributes.game_variant.data.attributes.name
                ? item.attributes.game_variant.data.attributes.name
                : 'Unknown';
        const players = item.attributes.score ?? 0;
        if (!summary[dateHour]) summary[dateHour] = {sessions: {}, players: 0};
        summary[dateHour].sessions[game] = (summary[dateHour].sessions[game] || 0) + 1;
        summary[dateHour].players += players;
    });
    return summary;
};

const generateSummaryCSV = (summaryObj) => {
    const gameSet = new Set();
    Object.keys(summaryObj).forEach((group) => {
        Object.keys(summaryObj[group].sessions).forEach((game) => gameSet.add(game));
    });
    const games = Array.from(gameSet).sort();
    let csv = 'Group,' + games.join(',') + ',Total Games,Total Players\n';
    Object.keys(summaryObj)
        .sort()
        .forEach((group) => {
            let total = 0;
            let row = group;
            games.forEach((game) => {
                const count = summaryObj[group].sessions[game] || 0;
                total += count;
                row += ',' + count;
            });
            const totalPlayers = summaryObj[group].players;
            row += ',' + total + ',' + totalPlayers;
            csv += row + '\n';
        });
    return csv;
};

const generateDailySummaryCSV = (data) => generateSummaryCSV(processDailySummary(data));
const generateHourlySummaryCSV = (data) => generateSummaryCSV(processHourlySummary(data));
const generateSuccessSummaryCSV = (data) => generateSummaryCSV(processSuccessSummary(data));
const generateHourlySuccessSummaryCSV = (data) => generateSummaryCSV(processHourlySuccessSummary(data));

// Enhanced daily summary processing with detailed player/session counts and active hours
const processEnhancedDailySummary = (data) => {
    const summary = {};

    data.forEach((item) => {
        const createdAt = item.attributes.createdAt;
        if (!createdAt) return;

        const day = formatDate(createdAt);
        const game = item.attributes.game_variant &&
            item.attributes.game_variant.data &&
            item.attributes.game_variant.data.attributes &&
            item.attributes.game_variant.data.attributes.name
                ? item.attributes.game_variant.data.attributes.name
                : 'Unknown';

        const players = item.attributes.score ?? 0;
        const gameStartDate = item.attributes.gameStartDate;

        if (!summary[day]) {
            summary[day] = {
                gamePlayers: {},
                gameSessions: {},
                totalPlayers: 0,
                totalSessions: 0,
                gameTimes: [] // Track precise game times for active hours calculation
            };
        }

        // Track players and sessions per game
        summary[day].gamePlayers[game] = (summary[day].gamePlayers[game] || 0) + players;
        summary[day].gameSessions[game] = (summary[day].gameSessions[game] || 0) + 1;
        summary[day].totalPlayers += players;
        summary[day].totalSessions += 1;

        // Track game times for active hours calculation (using minutes for precision)
        if (gameStartDate && players > 0) {
            const gameDate = new Date(gameStartDate);
            if (!isNaN(gameDate)) {
                // Store the actual timestamp for more precise calculation
                if (!summary[day].gameTimes) {
                    summary[day].gameTimes = [];
                }
                summary[day].gameTimes.push({
                    timestamp: gameDate.getTime(),
                    players: players,
                    game: game
                });
            }
        }
    });

    // Calculate active hours for each day
    Object.keys(summary).forEach(day => {
        summary[day].activeHours = calculateActiveHours(summary[day].gameTimes);
    });

    return summary;
};

// Calculate active hours: from first 2 consecutive games with players to last game with players
// Using precise timestamps for minute-level accuracy
const calculateActiveHours = (gameTimes) => {
    if (!gameTimes || gameTimes.length === 0) return 0;

    // Sort games by timestamp
    const sortedGames = gameTimes
        .filter(game => game.players > 0) // Only games with players
        .sort((a, b) => a.timestamp - b.timestamp);

    if (sortedGames.length < 2) return 0;

    // Find first occurrence of 2 consecutive games (within reasonable time frame)
    // Consider games "consecutive" if they're within 2 hours of each other
    const MAX_GAP_MS = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
    let firstConsecutiveStart = null;

    for (let i = 0; i < sortedGames.length - 1; i++) {
        const currentGame = sortedGames[i];
        const nextGame = sortedGames[i + 1];

        // Check if games are within reasonable consecutive timeframe
        const timeDiff = nextGame.timestamp - currentGame.timestamp;
        if (timeDiff <= MAX_GAP_MS) {
            firstConsecutiveStart = currentGame.timestamp;
            break;
        }
    }

    // If no consecutive games found, return 0
    if (firstConsecutiveStart === null) return 0;

    // Find last game with players
    const lastGameTimestamp = sortedGames[sortedGames.length - 1].timestamp;

    // Calculate active time in milliseconds
    const activeTimeMs = lastGameTimestamp - firstConsecutiveStart;

    // Convert to hours with decimal precision
    const activeHours = activeTimeMs / (1000 * 60 * 60); // ms to hours

    return Math.max(0, activeHours);
};

// Generate enhanced CSV with detailed game statistics
const generateEnhancedDailySummaryCSV = (data) => {
    const summaryObj = processEnhancedDailySummary(data);

    // Get all unique games
    const gameSet = new Set();
    Object.keys(summaryObj).forEach((day) => {
        Object.keys(summaryObj[day].gamePlayers).forEach((game) => gameSet.add(game));
    });
    const games = Array.from(gameSet).sort();

    // Build header
    let header = 'Group';

    // Add player columns for each game
    games.forEach(game => {
        header += ',' + game.replace(/,/g, '') + '_Players';
    });
    header += ',Total_Players';

    // Add session columns for each game
    games.forEach(game => {
        header += ',' + game.replace(/,/g, '') + '_Sessions';
    });
    header += ',Total_Sessions,Active_Hours';

    let csv = header + '\n';

    // Build data rows
    Object.keys(summaryObj)
        .sort()
        .forEach((day) => {
            let row = day;

            // Add player counts for each game
            games.forEach((game) => {
                const playerCount = summaryObj[day].gamePlayers[game] || 0;
                row += ',' + playerCount;
            });
            row += ',' + summaryObj[day].totalPlayers;

            // Add session counts for each game
            games.forEach((game) => {
                const sessionCount = summaryObj[day].gameSessions[game] || 0;
                row += ',' + sessionCount;
            });
            row += ',' + summaryObj[day].totalSessions;
            row += ',' + summaryObj[day].activeHours.toFixed(2);

            csv += row + '\n';
        });

    return csv;
};

const generateRowDataCSV = (data) => {
    const headers = [
        'ID',
        'Game Start Date',
        'Game End Date',
        'Day',
        'DurationSeconds',
        'Game',
        'NumberOfPlayers',
    ];
    let csv = headers.join(',') + '\n';
    data.forEach((item) => {
        const id = item.id || '';
        const gameStartDate = formatDateTime(item.attributes.gameStartDate);
        const gameEndDate = formatDateTime(item.attributes.gameEndDate);
        const day = item.attributes.gameStartDate ? formatDate(item.attributes.gameStartDate) : '';
        let durationSeconds = '';
        if (item.attributes.gameStartDate && item.attributes.gameEndDate) {
            try {
                const start = new Date(item.attributes.gameStartDate);
                const end = new Date(item.attributes.gameEndDate);
                durationSeconds = Math.floor((end - start) / 1000);
            } catch (e) {
                durationSeconds = '';
            }
        }
        const game =
            item.attributes.game_variant &&
            item.attributes.game_variant.data &&
            item.attributes.game_variant.data.attributes &&
            item.attributes.game_variant.data.attributes.name
                ? item.attributes.game_variant.data.attributes.name
                : 'Unknown';
        // Use score field as numberOfPlayers.
        const numPlayers = item.attributes.score ?? '';
        const row = [id, gameStartDate, gameEndDate, day, durationSeconds, game, numPlayers];
        csv += row.join(',') + '\n';
    });
    return csv;
};

const generateRowDataSuccessCSV = (data) => {
    const filtered = data.filter((item) => {
        if (!item.attributes.gameEndDate) return false;
        try {
            const start = new Date(item.attributes.gameStartDate);
            const end = new Date(item.attributes.gameEndDate);
            return (end - start) / 1000 > 40;
        } catch (e) {
            return false;
        }
    });
    return generateRowDataCSV(filtered);
};

// PDF Export functions
const exportChartAsImage = async (ref, fileName) => {
    if (ref.current) {
        // Clone the element
        const clone = ref.current.cloneNode(true);
        // Remove elements with the "no-print" class from the clone
        const noPrintElements = clone.querySelectorAll('.no-print');
        noPrintElements.forEach(el => el.remove());
        // Create a hidden container and append the clone
        const hiddenContainer = document.createElement('div');
        hiddenContainer.style.position = 'absolute';
        hiddenContainer.style.top = '-9999px';
        hiddenContainer.appendChild(clone);
        document.body.appendChild(hiddenContainer);
        // Use html2canvas on the clone
        const canvas = await html2canvas(clone, { scale: 2 });
        const imgData = canvas.toDataURL('image/png');
        // Remove the hidden container
        document.body.removeChild(hiddenContainer);
        // Create and click a download link for the image
        const link = document.createElement('a');
        link.href = imgData;
        link.download = fileName;
        link.click();
    }
};




const Home = ({darkMode, setDarkMode}) => {
    const {authData, logout} = useContext(AuthContext);
    const navigate = useNavigate();
    const [fromDateTime, setFromDateTime] = useState('');
    const [toDateTime, setToDateTime] = useState('');
    const [progress, setProgress] = useState(0);

    const [loading, setLoading] = useState(false);
    const [rawData, setRawData] = useState([]);
    const [grouping, setGrouping] = useState('day'); // 'day', 'hour', 'none'
    const [filter, setFilter] = useState('completed'); // 'completed', 'all'
    const [enhancedFormat, setEnhancedFormat] = useState(false); // Enhanced format with detailed stats

    // New inputs for Occupied Capacity Chart:
    const [workStart, setWorkStart] = useState('09'); // e.g. "09"
    const [workEnd, setWorkEnd] = useState('17');     // e.g. "17"
    const [maxPlayers, setMaxPlayers] = useState(10);

    // Refs for chart sections (for PDF export)
    const playTimeDayRef = useRef();
    const playTimeHourRef = useRef();
    const occupancyDayRef = useRef();
    const occupancyHourRef = useRef();
    const rowDataRef = useRef();
    const reportRef = useRef();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setRawData([]);
        let allData = [];
        let page = 1;
        const pageSize = 100;
        let totalPages = 1;
        try {
            const res1 = await fetchScores(authData.jwt, fromDateTime, toDateTime, 1, pageSize);
            allData = allData.concat(res1.data);
            totalPages = res1.meta.pagination.pageCount;
            setProgress(Math.round((1 / totalPages) * 100));
            for (page = 2; page <= totalPages; page++) {
                const res = await fetchScores(authData.jwt, fromDateTime, toDateTime, page, pageSize);
                allData = allData.concat(res.data);
                setProgress(Math.round((page / totalPages) * 100));
            }
            setRawData(allData);
        } catch (err) {
            alert('Error loading data: ' + err.message);
            console.error(err);
        } finally {
            setLoading(false);
            setProgress(100);
        }
    };

    const downloadCSVFile = (csvContent, filename) => {
        const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Download CSV based on user options.
    const handleDownload = () => {
        let csvContent = '';
        if (grouping === 'none') {
            if (filter === 'completed') {
                csvContent = generateRowDataSuccessCSV(rawData);
            } else {
                csvContent = generateRowDataCSV(rawData);
            }
        } else if (grouping === 'day') {
            if (enhancedFormat) {
                // Use enhanced format for daily summaries
                csvContent = generateEnhancedDailySummaryCSV(rawData);
            } else if (filter === 'completed') {
                csvContent = generateSuccessSummaryCSV(rawData);
            } else {
                csvContent = generateDailySummaryCSV(rawData);
            }
        } else if (grouping === 'hour') {
            if (filter === 'completed') {
                csvContent = generateHourlySuccessSummaryCSV(rawData);
            } else {
                csvContent = generateHourlySummaryCSV(rawData);
            }
        }
        downloadCSVFile(csvContent, 'summary.csv');
    };


    const exportPDFReport = async (refs) => {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();

        // Create a hidden container to hold the clean clone.
        const hiddenContainer = document.createElement('div');
        hiddenContainer.style.position = 'absolute';
        hiddenContainer.style.top = '-9999px';
        document.body.appendChild(hiddenContainer);

        for (let i = 0; i < refs.length; i++) {
            const original = refs[i].current;
            // Clone the node deeply.
            const clone = original.cloneNode(true);
            // Remove elements with class "no-print" from the clone.
            const noPrintElements = clone.querySelectorAll('.no-print');
            noPrintElements.forEach(el => el.remove());
            // Append clone to the hidden container.
            hiddenContainer.appendChild(clone);

            // Use html2canvas on the clone.
            const canvas = await html2canvas(clone, { scale: 2 });
            const imgData = canvas.toDataURL('image/png');
            const imgProps = pdf.getImageProperties(imgData);
            const pdfImgWidth = pageWidth;
            const pdfImgHeight = (imgProps.height * pdfImgWidth) / imgProps.width;
            pdf.addImage(imgData, 'PNG', 0, 0, pdfImgWidth, pdfImgHeight);

            // Remove the clone after capturing.
            hiddenContainer.removeChild(clone);

            if (i < refs.length - 1) pdf.addPage();
        }

        // Clean up hidden container.
        document.body.removeChild(hiddenContainer);

        const client = authData.user?.username || 'client';
        const dateTime = formatDateTimeForFilename();
        pdf.save(`${client}_${dateTime}_full_report.pdf`);
    };

    const handleDownloadPDF = async () => {
        const refs = [playTimeDayRef, playTimeHourRef, occupancyDayRef, occupancyHourRef, rowDataRef];
        await exportPDFReport(refs);
    };

    return (
        <Container sx={{mt: 4}}>
            <AppBar position="static">
                <Toolbar>
                    <Typography variant="h6" sx={{flexGrow: 1}}>
                        Welcome, {authData.user?.username || 'User'}
                    </Typography>
                    {/*<FormControlLabel*/}
                    {/*    control={<Switch checked={darkMode} onChange={() => setDarkMode(!darkMode)} color="default"/>}*/}
                    {/*    label="Dark Mode"*/}
                    {/*/>*/}
                    <Button color="inherit" onClick={handleLogout}>
                        Logout
                    </Button>
                </Toolbar>
            </AppBar>
            <Card sx={{mt: 3, p: 2}}>
                <CardContent>
                    <Typography variant="h5" gutterBottom>
                        Query Data (Date & Time)
                    </Typography>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="From"
                                    type="datetime-local"
                                    fullWidth
                                    slotProps={{
                                        inputLabel : {shrink: true}
                                    }}
                                    value={fromDateTime}
                                    onChange={(e) => setFromDateTime(e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="To"
                                    type="datetime-local"
                                    fullWidth
                                    slotProps={{
                                        inputLabel : {shrink: true}
                                    }}
                                    value={toDateTime}
                                    onChange={(e) => setToDateTime(e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button type="submit" variant="contained" fullWidth>
                                    Load Data
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                    {loading && <ProgressBar percentage={progress}/>}
                </CardContent>
            </Card>
            {/* New inputs for Occupied Capacity Chart */}
            <Card sx={{mt: 3, p: 2}}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Occupied Capacity Options
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Working Start Hour (0-23)"
                                type="number"
                                fullWidth
                                InputProps={{inputProps: {min: 0, max: 23}}}
                                value={workStart}
                                onChange={(e) => setWorkStart(e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Working End Hour (1-24)"
                                type="number"
                                fullWidth
                                InputProps={{inputProps: {min: 1, max: 24}}}
                                value={workEnd}
                                onChange={(e) => setWorkEnd(e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Max Players"
                                type="number"
                                fullWidth
                                InputProps={{inputProps: {min: 1}}}
                                value={maxPlayers}
                                onChange={(e) => setMaxPlayers(e.target.value)}
                                required
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            {rawData.length > 0 && (
                <>
                    <Card sx={{ mt: 3, p: 2, boxShadow: 3, borderRadius: 2 }} className="no-print">
                        <CardHeader
                            title="Download CSV & PDF Options"
                            sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText', borderRadius: 1 }}
                        />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <FormControl component="fieldset" fullWidth className="no-print">
                                        <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>
                                            Grouping
                                        </FormLabel>
                                        <RadioGroup row value={grouping} onChange={(e) => setGrouping(e.target.value)}>
                                            <FormControlLabel value="day" control={<Radio />} label="Per Day" />
                                            <FormControlLabel value="hour" control={<Radio />} label="Per Hour" />
                                            <FormControlLabel value="none" control={<Radio />} label="Row Data" />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControl component="fieldset" fullWidth className="no-print">
                                        <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>
                                            Filter
                                        </FormLabel>
                                        <RadioGroup row value={filter} onChange={(e) => setFilter(e.target.value)}>
                                            <FormControlLabel value="completed" control={<Radio />} label="Completed" />
                                            <FormControlLabel value="all" control={<Radio />} label="All" />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                                {grouping === 'day' && (
                                    <Grid item xs={12} md={4}>
                                        <FormControl component="fieldset" fullWidth className="no-print">
                                            <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>
                                                CSV Format
                                            </FormLabel>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={enhancedFormat}
                                                        onChange={(e) => setEnhancedFormat(e.target.checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label="Enhanced Format (with detailed stats & active hours)"
                                            />
                                        </FormControl>
                                    </Grid>
                                )}
                                <Grid item xs={12} md={grouping === 'day' ? 12 : 4}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={handleDownload}
                                        className="no-print"
                                        sx={{ textTransform: 'none', fontWeight: 'bold' }}
                                    >
                                        Download CSV
                                    </Button>
                                </Grid>
                            </Grid>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                sx={{ mt: 2, textTransform: 'none', fontWeight: 'bold' }}
                                onClick={handleDownloadPDF}
                                className="no-print"
                            >
                                Download Full Report as PDF
                            </Button>
                        </CardContent>
                    </Card>
                    <div id="report-content" ref={reportRef}>
                        {/* Page 1: Play Time Charts */}
                        <Card sx={{mt: 3}} ref={playTimeDayRef}>
                            <CardContent>
                                <SimpleGameTimeOccupancyChart
                                    rawData={rawData}
                                    workingStart={Number(workStart)}
                                    workingEnd={Number(workEnd)}
                                    grouping="day"
                                />
                                <Grid container spacing={1} sx={{mt: 1}} className="no-print">
                                    {/*<Grid item>*/}
                                    {/*    <Button variant="outlined"*/}
                                    {/*            onClick={() => downloadCSVFile(generateDailySummaryCSV(rawData), 'playtime_day.csv')}>*/}
                                    {/*        Export CSV*/}
                                    {/*    </Button>*/}
                                    {/*</Grid>*/}
                                    <Grid item>
                                        <Button
                                            variant="outlined"
                                            onClick={() => {
                                                const client = authData.user?.orga || 'client';
                                                const dateTime = formatDateTimeForFilename();
                                                exportChartAsImage(playTimeDayRef, `${client}_${dateTime}_playtime_day.png`);
                                            }}>
                                            Export Image
                                        </Button>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                        <Card sx={{mt: 3}} ref={playTimeHourRef}>
                            <CardContent>
                                <SimpleGameTimeOccupancyChart
                                    rawData={rawData}
                                    workingStart={Number(workStart)}
                                    workingEnd={Number(workEnd)}
                                    grouping="hour"
                                />
                                <Grid container spacing={1} sx={{mt: 1}} className="no-print">
                                    {/*<Grid item>*/}
                                    {/*    <Button variant="outlined"*/}
                                    {/*            onClick={() => downloadCSVFile(generateHourlySummaryCSV(rawData), 'playtime_hour.csv')}>*/}
                                    {/*        Export CSV*/}
                                    {/*    </Button>*/}
                                    {/*</Grid>*/}
                                    <Grid item>
                                        <Button variant="outlined"
                                                onClick={() => {
                                                    const client = authData.user?.username || 'client';
                                                    const dateTime = formatDateTimeForFilename();
                                                    exportChartAsImage(playTimeHourRef, `${client}_${dateTime}playtime_hour.png`);
                                                }}>
                                            Export Image
                                        </Button>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                        {/* Page 2: Occupancy Charts */}
                        <Card sx={{mt: 3}} ref={occupancyDayRef}>
                            <CardContent>
                                <OccupiedCapacityChart
                                    rawData={rawData}
                                    workingStart={Number(workStart)}
                                    workingEnd={Number(workEnd)}
                                    maxPlayers={Number(maxPlayers)}
                                    grouping="day"
                                />
                                <Grid container spacing={1} sx={{mt: 1}} className="no-print">
                                    {/*<Grid item>*/}
                                    {/*    <Button variant="outlined"*/}
                                    {/*            onClick={() => downloadCSVFile(generateDailySummaryCSV(rawData), 'occupancy_day.csv')}>*/}
                                    {/*        Export CSV*/}
                                    {/*    </Button>*/}
                                    {/*</Grid>*/}
                                    <Grid item>
                                        <Button variant="outlined"
                                                onClick={() => {
                                                    const client = authData.user?.username || 'client';
                                                    const dateTime = formatDateTimeForFilename();
                                                    exportChartAsImage(occupancyDayRef, `${client}_${dateTime}occupancy_day.png`);
                                                }}>
                                            Export Image
                                        </Button>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                        <Card sx={{mt: 3}} ref={occupancyHourRef}>
                            <CardContent>
                                <OccupiedCapacityChart
                                    rawData={rawData}
                                    workingStart={Number(workStart)}
                                    workingEnd={Number(workEnd)}
                                    maxPlayers={Number(maxPlayers)}
                                    grouping="hour"
                                />
                                <Grid container spacing={1} sx={{mt: 1}} className="no-print">
                                    {/*<Grid item>*/}
                                    {/*    <Button variant="outlined"*/}
                                    {/*            onClick={() => downloadCSVFile(generateHourlySummaryCSV(rawData), 'occupancy_hour.csv')}>*/}
                                    {/*        Export CSV*/}
                                    {/*    </Button>*/}
                                    {/*</Grid>*/}
                                    <Grid item>
                                        <Button variant="outlined"
                                                onClick={() => {
                                                    const client = authData.user?.username || 'client';
                                                    const dateTime = formatDateTimeForFilename();
                                                    exportChartAsImage(occupancyHourRef, `${client}_${dateTime}occupancy_hour.png`);
                                                }}>
                                            Export Image
                                        </Button>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                        {/* Page 3: Row Data Chart */}
                        <Card sx={{mt: 3}} ref={rowDataRef}>
                            <CardContent>
                                <RowDataChart rawData={rawData} title="Games Chart"/>
                                <Grid container spacing={1} sx={{mt: 1}} className="no-print">
                                    {/*<Grid item>*/}
                                    {/*    <Button variant="outlined"*/}
                                    {/*            onClick={() => downloadCSVFile(generateRowDataCSV(rawData), 'rowdata.csv')}>*/}
                                    {/*        Export CSV*/}
                                    {/*    </Button>*/}
                                    {/*</Grid>*/}
                                    <Grid item>
                                        <Button variant="outlined"
                                                onClick={() => {
                                                    const client = authData.user?.username || 'client';
                                                    const dateTime = formatDateTimeForFilename();
                                                    exportChartAsImage(rowDataRef, `${client}_${dateTime}games_chart.png`);
                                                }}>
                                            Export Image
                                        </Button>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                    </div>
                </>
            )}
        </Container>
    );
};

export default Home;
